/**
 * The tags a note may contain in storage. Anything else is a plain text note written
 * before the rich text editor existed, or markup we strip.
 */
const NOTE_TAGS = /<\/?(ul|ol|li|b|strong|i|em|u|br|div|p)\b[^>]*>/i;

/** An HTML entity, e.g. &amp; &lt; &#39; &#x27; */
const ENTITY = /&(amp|lt|gt|quot|apos|nbsp|#\d+|#x[0-9a-f]+);/i;

/**
 * Whether a stored note is markup rather than one of the plain text notes written
 * before the editor existed.
 *
 * Entities count as markup: a note holding "L&amp;D" contains no tags at all, but it
 * is already HTML, and escaping it a second time is what turns "L&D" into "L&amp;D"
 * on screen.
 */
export function looksLikeHtml(value: string): boolean {
  return NOTE_TAGS.test(value) || ENTITY.test(value);
}

/**
 * Undoes the extra escaping that earlier saves added, so a note stored as
 * "L&amp;amp;D" reads as "L&D" again.
 *
 * Only an "&" that is itself the start of an escaped entity is unwrapped, so a
 * correctly stored "L&amp;D" passes through untouched. Repeats until nothing is left
 * to unwrap, because a note re-saved several times gained a layer each time.
 */
export function normalizeRichText(value: string): string {
  if (!value) return value;

  let normalized = value;
  let previous: string;

  do {
    previous = normalized;
    normalized = normalized.replace(/&amp;(?=(?:amp|lt|gt|quot|apos|nbsp|#\d+|#x[0-9a-f]+);)/gi, '&');
  } while (normalized !== previous);

  return normalized;
}
